import * as React from "react"
import { Helmet } from "react-helmet"
import "../styles/home.css"
import resumePdf from "../../static/resume_6_1_26.pdf"
import portrait from "../../static/profile.png"

const LinkedInIcon = () => (
  <svg className="pill-icon" viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
    <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.03-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.95v5.66H9.35V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.07 2.07 0 1 1 0-4.13 2.07 2.07 0 0 1 0 4.13zM7.12 20.45H3.55V9h3.57v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.45C23.2 24 24 23.23 24 22.27V1.73C24 .77 23.2 0 22.22 0z"/>
  </svg>
)

const GitHubIcon = () => (
  <svg className="pill-icon" viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
    <path d="M12 .5C5.37.5 0 5.87 0 12.5c0 5.3 3.44 9.8 8.21 11.39.6.11.82-.26.82-.58 0-.29-.01-1.05-.02-2.06-3.34.73-4.04-1.61-4.04-1.61-.55-1.39-1.34-1.76-1.34-1.76-1.09-.75.08-.73.08-.73 1.2.08 1.84 1.24 1.84 1.24 1.07 1.83 2.81 1.3 3.5.99.11-.78.42-1.3.76-1.6-2.67-.3-5.47-1.34-5.47-5.93 0-1.31.47-2.39 1.24-3.23-.13-.3-.54-1.52.12-3.18 0 0 1.01-.32 3.3 1.23a11.5 11.5 0 0 1 6.01 0c2.29-1.55 3.29-1.23 3.29-1.23.66 1.66.25 2.88.12 3.18.77.84 1.24 1.92 1.24 3.23 0 4.6-2.8 5.62-5.48 5.92.43.37.81 1.1.81 2.22 0 1.61-.01 2.9-.01 3.29 0 .32.21.7.83.58A12 12 0 0 0 24 12.5C24 5.87 18.63.5 12 .5z"/>
  </svg>
)

const EmailIcon = () => (
  <svg className="pill-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2"/>
    <path d="m3 7 9 6 9-6"/>
  </svg>
)

const ResumeIcon = () => (
  <svg className="pill-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/>
    <path d="M14 3v5h5"/>
    <path d="M9 13h6M9 17h6"/>
  </svg>
)

const siteUrl = "https://www.kevincoleman.net"
const ogImage = `${siteUrl}/profile.png`
const pageTitle = "Kevin Coleman - Software engineer and team leader"
const pageDescription =
  "A personal homepage for Kevin Coleman, a software engineer and team leader focused on Rails, JavaScript, data, automation, and AI-enabled product work."

const personSchema = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: "Kevin Coleman",
  url: siteUrl,
  image: ogImage,
  jobTitle: "Principal Engineer",
  worksFor: { "@type": "Organization", name: "Spaceback (Rembrand)" },
  address: { "@type": "PostalAddress", addressLocality: "Seattle", addressRegion: "WA" },
  email: "mailto:kevin@kevincoleman.net",
  sameAs: [
    "https://www.linkedin.com/in/kevinjamescoleman/",
    "https://github.com/kevinjcoleman",
  ],
}

const Index = () => {
  return (
    <main className="page">
      <Helmet>
        <html lang="en" />
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="author" content="Kevin Coleman" />
        <meta name="theme-color" content="#2f64dd" />
        <link rel="canonical" href={siteUrl} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Kevin Coleman" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={siteUrl} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:alt" content="Kevin Coleman" />
        <meta property="og:locale" content="en_US" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={ogImage} />

        <script type="application/ld+json">{JSON.stringify(personSchema)}</script>
      </Helmet>
      <header className="topbar" aria-label="Primary">
        <a className="wordmark" href="#top">Kevin Coleman</a>
        <nav className="nav" aria-label="Sections">
          <a href="#summary">Summary</a>
          <a href="#timeline">Timeline</a>
          <a href="#projects">Projects</a>
          <a href="#links">Links</a>
        </nav>
      </header>

      <section className="hero" id="top" aria-labelledby="hero-title">
        <div className="hero-text">
          <p className="eyebrow">Seattle, WA / Software engineer and team leader</p>
          <h1 id="hero-title">Software engineer building useful product systems.</h1>
          <p className="intro">Nearly a decade across full-stack Rails, modern JavaScript, data workflows, API integration, automation, and AI-driven product features.</p>
          <div className="contact-strip" aria-label="Contact">
            <a className="pill primary" href="mailto:kevin@kevincoleman.net"><EmailIcon />Email Me</a>
            <a className="pill" href={resumePdf}><ResumeIcon />Resume</a>
            <a className="pill" href="https://www.linkedin.com/in/kevinjamescoleman/"><LinkedInIcon />LinkedIn</a>
            <a className="pill" href="https://github.com/kevinjcoleman"><GitHubIcon />GitHub</a>
          </div>
        </div>
        <img className="portrait" src={portrait} alt="Kevin Coleman" width="140" height="140" />
      </section>

      <section className="section" id="bio" aria-labelledby="bio-label">
        <h2 className="section-label" id="bio-label">Bio</h2>
        <div>
          <p className="bio">Kevin is a software engineer and team leader who turns ambiguous product requests into shipped systems. He has built full-stack web applications, public APIs, data pipelines, testing workflows, and deployment paths across several product teams, with recent work focused on practical AI features including generative content, autonomous agents, automations, and MCP tooling.</p>
        </div>
      </section>

      <section className="section" id="summary" aria-labelledby="summary-label">
        <h2 className="section-label" id="summary-label">Summary</h2>
        <div>
          <ul className="summary-list">
            <li><b>Full-stack product engineering</b><span>Rails, JavaScript, APIs, data models, front-end flows, and production delivery.</span></li>
            <li><b>Team and feature leadership</b><span>Scopes ambiguous requests, aligns implementation, and carries work from idea to launch.</span></li>
            <li><b>Automation and data</b><span>Uses integrations and data-driven workflows to reduce repetitive work and improve decisions.</span></li>
            <li><b>AI-enabled systems</b><span>Ships practical AI features with clear review paths, tests, and product judgment.</span></li>
          </ul>
        </div>
      </section>

      <section className="section" id="timeline" aria-labelledby="timeline-label">
        <h2 className="section-label" id="timeline-label">Career Timeline</h2>
        <div>
          <ol className="timeline">
            <li><span className="period">2023-present</span><span><span className="role">Principal Engineer</span><span className="org">Spaceback (Rembrand)</span></span></li>
            <li><span className="period">2022-2023</span><span><span className="role">Staff Software Engineer</span><span className="org">Brightline</span></span></li>
            <li><span className="period">2020-2022</span><span><span className="role">Senior Software Engineer / Engineering Manager</span><span className="org">Cerebral</span></span></li>
            <li><span className="period">2017-2020</span><span><span className="role">Software Engineer / Senior Software Engineer</span><span className="org">LegalNature</span></span></li>
            <li><span className="period">2014-2017</span><span><span className="role">Services Engineer / Services Manager</span><span className="org">NationBuilder</span></span></li>
          </ol>
        </div>
      </section>

      <section className="section" id="projects" aria-labelledby="projects-label">
        <h2 className="section-label" id="projects-label">Project Gallery</h2>
        <div>
          <div className="project-grid">
            <article className="project">
              <div>
                <span className="meta">2020-present</span>
                <h3>Recipio</h3>
                <p>Imports recipes from URLs, photos, and voice or text notes, auto-extracts and categorizes ingredients and instructions, with AI-driven shopping lists, meal planning, and a live cooking assistant.</p>
              </div>
              <a href="https://recipio.app">recipio.app</a>
            </article>
          </div>
        </div>
      </section>

      <footer className="footer" id="links">
        <h2 className="section-label">Social Links</h2>
        <div>
          <p>Outside of work: hiking, gardening, water polo, and family.</p>
          <div className="social-strip" aria-label="Social links">
            <a className="pill primary" href="mailto:kevin@kevincoleman.net"><EmailIcon />Email Me</a>
            <a className="pill" href={resumePdf}><ResumeIcon />Resume</a>
            <a className="pill" href="https://www.linkedin.com/in/kevinjamescoleman/"><LinkedInIcon />LinkedIn</a>
            <a className="pill" href="https://github.com/kevinjcoleman"><GitHubIcon />GitHub</a>
          </div>
        </div>
      </footer>
    </main>
  )
}

export default Index
